import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Waiting, said out loud.
//
// Making a picture is two waits, not one, and the studio used to name
// neither. The model draws for about twenty seconds, which is a button that
// sits there saying nothing. Then the file goes to the uploads bucket and
// the render plane probes and normalises it exactly as it does an upload,
// which is another minute — and that half was invisible, because nothing
// polled for the result. A creator pressed a button, got a spinner, then got
// a screen that looked finished and was not: the face only ever appeared on
// a reload.
//
// So: one clock across both halves, in words that say which half is running,
// and a poll that fills the picture in when it lands.
// ---------------------------------------------------------------------------

/** What to expect before pressing, in one clause, so every bench says it the same way. */
export const PICTURE_WAIT = 'about twenty seconds to draw, then a minute or two while the render plane looks at it';

/** How often to ask whether a picture has finished promoting. */
export const LOOK_EVERY_MS = 4000;

/**
 * How many times to ask before giving up — eight minutes at the rate above.
 *
 * It was three, which was measured against a promotion that took sixty-six
 * seconds. The next one took three minutes and twenty-two: the render plane
 * runs as a Cloud Run job, so a cold one spends its first ninety seconds
 * pulling the image before it looks at anything. The watch gave up twenty-two
 * seconds early, silently, and the creator was back to reloading to find out
 * it had worked — the exact failure this was written to end. A bound has to
 * clear the worst case, not the one that was measured first.
 */
export const GIVE_UP_AFTER = 120;

/** Seconds since `startedAt`, ticking while it is set; 0 when it is not. */
export function useElapsed(startedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  return startedAt === null ? 0 : Math.max(0, Math.round((now - startedAt) / 1000));
}

/** A count a person reads at a glance: "18s", "1m 04s". */
export function saySeconds(s: number): string {
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

/**
 * Which half of the wait is running, in the creator's words.
 *
 * The cut-over is what the request itself reports: while the call is still
 * open the model is drawing, and once it has answered the render plane has
 * it. Never a guess from the clock — a slow draw would otherwise be
 * announced as a promotion that had not started.
 */
export function sayWait(drawing: boolean, seconds: number): string {
  return drawing ? `Drawing… ${saySeconds(seconds)}` : `Developing… ${saySeconds(seconds)}`;
}

/** What the card says when the watch has run out — never silence. */
export const GAVE_UP = 'This is taking longer than it should. The picture may still be coming.';
